import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Check, Clock, Shield, Globe, Calendar, 
  FileText, Users, ArrowRight, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getVisa, getProperties } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import PropertyCard from '../components/PropertyCard';

export default function VisaDetailPage() {
  const { visaId } = useParams();
  const [visa, setVisa] = useState(null);
  const [eligibleProperties, setEligibleProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const visaData = await getVisa(visaId);
        setVisa(visaData);

        // Fetch properties eligible for this visa
        const properties = await getProperties({ country: visaData.country });
        setEligibleProperties(properties.filter(p => p.visa_eligible).slice(0, 3));
      } catch (error) {
        console.error('Error fetching visa:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [visaId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] pt-20 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!visa) {
    return (
      <div className="min-h-screen bg-[#0B0C10] pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-4">Visa program not found</h2>
          <Link to="/visas">
            <Button className="btn-luxury">Back to Visa Programs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="visa-detail-page">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link 
          to="/visas" 
          className="inline-flex items-center text-[#A1A1AA] hover:text-white transition-colors"
          data-testid="back-to-visas"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Visa Programs
        </Link>
      </div>

      {/* Hero */}
      <section className="relative h-[40vh] max-h-[400px]">
        <img
          src={visa.image}
          alt={visa.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/60 to-transparent" />
        
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Badge className="bg-[#D4AF37] text-[#0B0C10] mb-4">
              {visa.type}
            </Badge>
            <h1 className="text-4xl sm:text-5xl text-white mb-2" data-testid="visa-title">
              {visa.name}
            </h1>
            <div className="flex items-center text-[#A1A1AA]">
              <MapPin className="h-5 w-5 mr-2" />
              {visa.country} • {visa.region}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                  <div className="text-[#D4AF37] font-semibold text-lg">
                    {formatPrice(visa.min_investment)}
                  </div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mt-1">
                    Min. Investment
                  </div>
                </div>
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                  <div className="text-white font-semibold text-lg">
                    {visa.processing_time}
                  </div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mt-1">
                    Processing
                  </div>
                </div>
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                  <div className="text-white font-semibold text-lg">
                    {visa.validity}
                  </div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mt-1">
                    Validity
                  </div>
                </div>
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                  <div className="text-white font-semibold text-lg flex items-center justify-center">
                    <Users className="h-5 w-5 mr-1" />
                    Yes
                  </div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mt-1">
                    Family Included
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-2xl text-white mb-6 flex items-center">
                  <Shield className="h-6 w-6 text-[#D4AF37] mr-3" />
                  Key Benefits
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visa.benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start bg-[#1A1D24] border border-[#2A2F3A] rounded-lg p-4"
                    >
                      <Check className="h-5 w-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-white">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Requirements */}
              <div className="mb-12">
                <h2 className="text-2xl text-white mb-6 flex items-center">
                  <FileText className="h-6 w-6 text-[#D4AF37] mr-3" />
                  Requirements
                </h2>
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                  <ul className="space-y-4">
                    {visa.requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start text-[#A1A1AA]">
                        <span className="w-6 h-6 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-sm flex items-center justify-center mr-3 flex-shrink-0">
                          {index + 1}
                        </span>
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Process Timeline */}
              <div>
                <h2 className="text-2xl text-white mb-6 flex items-center">
                  <Clock className="h-6 w-6 text-[#D4AF37] mr-3" />
                  Application Process
                </h2>
                <div className="space-y-4">
                  {[
                    { step: 1, title: 'Initial Consultation', desc: 'Free assessment of eligibility and program selection' },
                    { step: 2, title: 'Property Selection', desc: 'Choose from our curated portfolio of qualifying properties' },
                    { step: 3, title: 'Documentation', desc: 'We assist with all required paperwork and legal formalities' },
                    { step: 4, title: 'Application Submission', desc: 'Submit application to relevant authorities' },
                    { step: 5, title: 'Approval & Residency', desc: 'Receive your residency permit and enjoy your new status' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.step}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-[#0B0C10] font-semibold flex items-center justify-center flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="ml-4 flex-1 pb-8 border-l-2 border-[#2A2F3A] pl-6 -mt-1">
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-[#A1A1AA] text-sm mt-1">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="sticky top-28"
            >
              <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6 mb-6">
                <h3 className="text-lg text-white mb-6">Get Started</h3>
                
                <div className="space-y-4 mb-6">
                  <Link to="/contact" className="block">
                    <Button className="w-full btn-luxury" data-testid="visa-enquire-btn">
                      Schedule Consultation
                    </Button>
                  </Link>
                  <Link to={`/properties?country=${encodeURIComponent(visa.country)}`}>
                    <Button variant="outline" className="w-full border-[#2A2F3A] text-white hover:bg-[#2A2F3A]">
                      View Eligible Properties
                    </Button>
                  </Link>
                </div>

                <div className="border-t border-[#2A2F3A] pt-6">
                  <div className="text-sm text-[#A1A1AA] mb-4">Why Choose Mulberry World?</div>
                  <ul className="space-y-3 text-sm text-white">
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-[#D4AF37] mr-2" />
                      100% Visa Success Rate
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-[#D4AF37] mr-2" />
                      End-to-end support
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-[#D4AF37] mr-2" />
                      Local legal partners
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-[#D4AF37] mr-2" />
                      Post-visa assistance
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-xl p-6">
                <h4 className="text-white font-medium mb-2">Have Questions?</h4>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Our residency experts are here to help you navigate the process.
                </p>
                <a href="tel:+918999570132" className="text-[#D4AF37] text-sm hover:underline">
                  +91 89995 70132
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Eligible Properties */}
      {eligibleProperties.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-[#2A2F3A]">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl text-white">Eligible Properties in {visa.country}</h2>
            <Link 
              to={`/properties?country=${encodeURIComponent(visa.country)}`}
              className="text-[#D4AF37] hover:text-[#F7E7CE] flex items-center"
            >
              View All <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {eligibleProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
